import * as React from "react"
import { router } from "@inertiajs/react"
import { LayoutGrid, List, X } from "lucide-react"
import { Checkbox } from "@/components/ui/checkbox"
import { Select } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { CLIP_STATUSES, CLIP_STATUS_LABELS, type ClipFilters, type ClipSortKey, type ClipTypeRef, type ClipView, type TagRef } from "./types"

// Dropdown sort options for grid view. List view sorts via clickable column
// headers (which write to the same `sort` URL param but cover more columns
// and both directions).
const SORT_LABELS: Partial<Record<ClipSortKey, string>> = {
  recently_recorded: "Recently recorded",
  recently_synced:   "Recently synced",
  title:             "Title (A→Z)",
}

const STATUS_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "enriched",              label: CLIP_STATUS_LABELS.enriched },
  { value: "queued_for_enrichment", label: CLIP_STATUS_LABELS.queued_for_enrichment },
  { value: "needs_processing",      label: CLIP_STATUS_LABELS.needs_processing },
  { value: "deprecated",            label: CLIP_STATUS_LABELS.deprecated },
  { value: "deprecation_candidates", label: "Deprecation candidates (90+ days)" },
  ...CLIP_STATUSES
    .filter((s) => !["enriched", "queued_for_enrichment", "needs_processing", "deprecated"].includes(s))
    .map((s) => ({ value: s, label: CLIP_STATUS_LABELS[s] })),
  { value: "all", label: "All statuses" },
]

export function ClipsHeader({
  filters,
  sort,
  view,
  allTags,
  allFolders,
  clipTypes,
  totalCount,
  selectAllState,
  onToggleSelectAll,
  bulkActions,
}: {
  filters: ClipFilters
  sort: ClipSortKey
  view: ClipView
  allTags: TagRef[]
  allFolders: string[]
  clipTypes: ClipTypeRef[]
  totalCount: number
  selectAllState: "none" | "some" | "all"
  onToggleSelectAll: () => void
  bulkActions?: React.ReactNode
}) {
  const selectAllRef = React.useRef<HTMLInputElement>(null)
  React.useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectAllState === "some"
    }
  }, [selectAllState])
  function commitFilter(updates: Partial<ClipFilters & { sort: ClipSortKey; view: ClipView }>) {
    const next = { ...filters, sort, view, ...updates }
    router.get("/clips", next, { preserveState: true, preserveScroll: true, replace: true })
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md border border-hairline bg-surface px-2.5" title="Select all currently shown">
            <Checkbox
              ref={selectAllRef}
              checked={selectAllState === "all"}
              onChange={onToggleSelectAll}
              aria-label="Select all currently shown"
            />
          </label>

          <Select
            value={filters.status}
            onChange={(e) => commitFilter({ status: e.target.value })}
            aria-label="Filter by status"
            className="w-36"
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>

          <Select
            value={filters.folder_path ?? ""}
            onChange={(e) => commitFilter({ folder_path: e.target.value || null })}
            aria-label="Filter by folder"
            className="w-40"
          >
            <option value="">All folders</option>
            {allFolders.map((folder) => (
              <option key={folder} value={folder}>{folder}</option>
            ))}
          </Select>

          {clipTypes.length > 0 && (
            <Select
              value={String(filters.clip_type_id ?? "")}
              onChange={(e) => commitFilter({ clip_type_id: e.target.value || null })}
              aria-label="Filter by clip type"
              className="w-40"
            >
              <option value="">All clip types</option>
              {clipTypes.map((clipType) => (
                <option key={clipType.id} value={clipType.id}>{clipType.name}</option>
              ))}
              <option value="none">No clip type</option>
            </Select>
          )}

          <Select
            value={String(filters.tag_id ?? "")}
            onChange={(e) => commitFilter({ tag_id: e.target.value || null })}
            aria-label="Filter by tag"
            className="w-36"
          >
            <option value="">All tags</option>
            {allTags.map((tag) => (
              <option key={tag.id} value={tag.id}>{tag.name}</option>
            ))}
          </Select>

          <Select
            value={sort}
            onChange={(e) => commitFilter({ sort: e.target.value as ClipSortKey })}
            aria-label="Sort"
            className="w-44"
          >
            {(Object.keys(SORT_LABELS) as ClipSortKey[]).map((k) => (
              <option key={k} value={k}>{SORT_LABELS[k]}</option>
            ))}
          </Select>

          {filters.q && (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-hairline bg-surface px-2 py-1 text-xs text-ink-body hover:text-ink-display"
              onClick={() => commitFilter({ q: "" })}
              aria-label="Clear search"
            >
              <span>Search: <strong>{filters.q}</strong></span>
              <X className="h-3 w-3" />
            </button>
          )}

          {bulkActions}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-ink-muted">{totalCount} clip{totalCount === 1 ? "" : "s"}</span>

          <div className="flex overflow-hidden rounded-md border border-hairline">
            <button
              type="button"
              onClick={() => commitFilter({ view: "grid" })}
              className={cn(
                "flex h-8 w-9 cursor-pointer items-center justify-center text-ink-muted",
                view === "grid" && "bg-surface text-ink-display",
              )}
              aria-label="Grid view"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => commitFilter({ view: "list" })}
              className={cn(
                "flex h-8 w-9 cursor-pointer items-center justify-center text-ink-muted",
                view === "list" && "bg-surface text-ink-display",
              )}
              aria-label="List view"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

    </div>
  )
}
